import {
  getStore,
  addSelectedElement,
  removeSelectedElement,
  isElementSelected,
  type SelectedElement,
} from '../core/state';
import {
  showHoverHighlight,
  hideHoverHighlight,
  addSelectionOutline,
  removeSelectionOutline,
  isWidgetElement,
} from './overlay';
import { generateUniqueSelector, generateXPath } from '../utils/unique-selector';
import { truncateText } from '../utils/sanitize';
import {
  debugLogWidgetGuard,
  debugSetLastDecision,
  debugSetLastEvent,
  debugSetLastResolvedTarget,
  debugSetLastTap,
  debugSetSelectMode,
  debugSetSelectedCount,
  logBugWidgetDebug,
} from './debug';

let active = false;
let selectorOverlay: HTMLDivElement | null = null;
let selectionListener: ((element: SelectedElement) => void) | null = null;

const WIDGET_ATTR = 'data-br-widget';
const DEDUPE_WINDOW_MS = 220;

let lastSelection = {
  x: Number.NaN,
  y: Number.NaN,
  at: 0,
};

let lastPreviewTarget: HTMLElement | null = null;

/**
 * Get the real page element under the cursor by temporarily hiding the overlay.
 */
function getElementUnderCursor(x: number, y: number): HTMLElement | null {
  if (!selectorOverlay) return null;
  selectorOverlay.style.display = 'none';
  const el = document.elementFromPoint(x, y) as HTMLElement | null;
  selectorOverlay.style.display = 'block';
  return el;
}

function isRecentDuplicateSelection(x: number, y: number): boolean {
  const now = Date.now();
  const duplicate =
    Math.abs(x - lastSelection.x) <= 1
    && Math.abs(y - lastSelection.y) <= 1
    && now - lastSelection.at <= DEDUPE_WINDOW_MS;

  if (!duplicate) {
    lastSelection = { x, y, at: now };
  }

  return duplicate;
}

function tryVibrateSelection(): void {
  if (!('vibrate' in navigator) || typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate(10);
  } catch {
    // no-op
  }
}

function resolveValidTarget(x: number, y: number): HTMLElement | null {
  const target = getElementUnderCursor(x, y);
  debugSetLastResolvedTarget(target);
  if (!target) {
    debugSetLastDecision('ignored', 'no-target');
    return null;
  }

  const widget = isWidgetElement(target);
  debugLogWidgetGuard(target, widget);
  if (widget) {
    debugSetLastDecision('ignored', 'widget-element');
    return null;
  }

  return target;
}

function commitSelection(target: HTMLElement | null, x: number, y: number, source: string): void {
  logBugWidgetDebug('preview_commit', {
    source,
    x: Math.round(x),
    y: Math.round(y),
    target: target ? target.tagName.toLowerCase() : null,
  });

  if (!target) {
    debugSetLastDecision('ignored', 'no-target');
    return;
  }

  if (isElementSelected(target)) {
    removeSelectedElement(target);
    removeSelectionOutline(target);
    debugSetSelectedCount(getStore().selectedElements.length);
    debugSetLastDecision('ignored', 'already-selected-toggle-off');
    return;
  }

  // Replace previous selection
  const store = getStore();
  for (const sel of [...store.selectedElements]) {
    removeSelectionOutline(sel.element);
    removeSelectedElement(sel.element);
  }

  const info = captureElementInfo(target);
  addSelectedElement(info);
  addSelectionOutline(target);
  debugSetSelectedCount(getStore().selectedElements.length);
  debugSetLastDecision('selected', 'selection-added');
  tryVibrateSelection();
  selectionListener?.(info);
  logBugWidgetDebug('selection_callback_fired', { selector: info.selector });
}

function selectAtPoint(x: number, y: number): void {
  debugSetLastTap(x, y);
  const target = resolveValidTarget(x, y);
  commitSelection(target, x, y, 'point');
}

function updatePreviewAtPoint(x: number, y: number, source: string): HTMLElement | null {
  debugSetLastTap(x, y);
  const target = resolveValidTarget(x, y);
  logBugWidgetDebug('preview_update', {
    source,
    x: Math.round(x),
    y: Math.round(y),
    target: target ? target.tagName.toLowerCase() : null,
  });

  if (!target) {
    hideHoverHighlight();
    return null;
  }

  const rect = target.getBoundingClientRect();
  showHoverHighlight(rect);
  lastPreviewTarget = target;
  return target;
}

function selectFromInteraction(x: number, y: number, source: string): void {
  logBugWidgetDebug('selection_interaction_source', { source, x: Math.round(x), y: Math.round(y) });
  if (isRecentDuplicateSelection(x, y)) {
    logBugWidgetDebug('selection_interaction_ignored_duplicate', { source });
    return;
  }
  selectAtPoint(x, y);
}

function commitFromTouchInteraction(x: number, y: number, source: string): void {
  logBugWidgetDebug('selection_interaction_source', { source, x: Math.round(x), y: Math.round(y) });
  if (isRecentDuplicateSelection(x, y)) {
    logBugWidgetDebug('selection_interaction_ignored_duplicate', { source });
    return;
  }

  const currentTarget = resolveValidTarget(x, y);
  const targetToCommit = currentTarget || lastPreviewTarget;
  commitSelection(targetToCommit, x, y, source);
  lastPreviewTarget = null;
}

function onOverlayMouseMove(e: MouseEvent): void {
  updatePreviewAtPoint(e.clientX, e.clientY, 'overlay:mousemove');
}

function onOverlayMouseDown(e: MouseEvent): void {
  debugSetLastEvent('mousedown', 'mouse');
  logBugWidgetDebug('overlay_handler_mousedown');
  e.preventDefault();
}

function onOverlayClick(e: MouseEvent): void {
  debugSetLastEvent('click', 'mouse');
  logBugWidgetDebug('overlay_handler_click');
  e.preventDefault();
  e.stopPropagation();
  selectFromInteraction(e.clientX, e.clientY, 'overlay:click');
}

function onOverlayPointerUp(e: PointerEvent): void {
  debugSetLastEvent('pointerup', e.pointerType || '');
  logBugWidgetDebug('overlay_handler_pointerup', { pointerType: e.pointerType || '' });
  // Keep desktop behavior on click; use pointerup for touch/pen reliability.
  if (e.pointerType === 'mouse') return;
  e.preventDefault();
  e.stopPropagation();
  selectFromInteraction(e.clientX, e.clientY, `overlay:pointerup:${e.pointerType || 'unknown'}`);
}

function onOverlayTouchStart(e: TouchEvent): void {
  debugSetLastEvent('touchstart', 'touch');
  logBugWidgetDebug('overlay_handler_touchstart');
  // Prevent scrolling/gesture interference while selecting on mobile.
  e.preventDefault();

  const touch = e.touches[0];
  if (!touch) return;
  updatePreviewAtPoint(touch.clientX, touch.clientY, 'overlay:touchstart');
}

function onOverlayTouchMove(e: TouchEvent): void {
  debugSetLastEvent('touchmove', 'touch');
  logBugWidgetDebug('overlay_handler_touchmove', { touches: e.touches.length });
  e.preventDefault();

  const touch = e.touches[0];
  if (!touch) return;
  updatePreviewAtPoint(touch.clientX, touch.clientY, 'overlay:touchmove');
}

function onOverlayTouchEnd(e: TouchEvent): void {
  debugSetLastEvent('touchend', 'touch');
  logBugWidgetDebug('overlay_handler_touchend', { touches: e.changedTouches.length });
  const touch = e.changedTouches[0];
  if (!touch) {
    debugSetLastDecision('ignored', 'touchend-no-touch');
    return;
  }
  e.preventDefault();
  e.stopPropagation();
  commitFromTouchInteraction(touch.clientX, touch.clientY, 'overlay:touchend');
}

function isDesktopFinePointerMode(): boolean {
  if (typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function isSelectorOverlayTarget(target: EventTarget | null): boolean {
  const el = target as Element | null;
  if (!el || !selectorOverlay) return false;
  return el === selectorOverlay || selectorOverlay.contains(el);
}

function isWidgetControlTarget(target: EventTarget | null): boolean {
  if (isSelectorOverlayTarget(target)) return false;
  const el = target as Element | null;
  if (!el) return false;
  const widget = isWidgetElement(el);
  debugLogWidgetGuard(el, widget);
  return widget;
}

function isOverlayOrWidgetTarget(target: EventTarget | null): boolean {
  if (isSelectorOverlayTarget(target)) {
    return true;
  }
  return isWidgetControlTarget(target);
}

function onDocumentClickFallback(e: MouseEvent): void {
  if (!active) return;
  if (isOverlayOrWidgetTarget(e.target)) {
    logBugWidgetDebug('document_fallback_click_ignored_widget_or_overlay');
    return;
  }
  debugSetLastEvent('document:click:fallback', 'mouse');
  logBugWidgetDebug('document_fallback_click');
  e.preventDefault();
  selectFromInteraction(e.clientX, e.clientY, 'document:click');
}

function onDocumentPointerUpFallback(e: PointerEvent): void {
  if (!active) return;
  if (isOverlayOrWidgetTarget(e.target)) {
    logBugWidgetDebug('document_fallback_pointerup_ignored_widget_or_overlay');
    return;
  }
  debugSetLastEvent('document:pointerup:fallback', e.pointerType || '');
  logBugWidgetDebug('document_fallback_pointerup', { pointerType: e.pointerType || '' });
  if (e.pointerType === 'mouse') return;
  e.preventDefault();
  selectFromInteraction(e.clientX, e.clientY, `document:pointerup:${e.pointerType || 'unknown'}`);
}

function onDocumentTouchMoveFallback(e: TouchEvent): void {
  if (!active) return;
  if (isOverlayOrWidgetTarget(e.target)) {
    return;
  }

  const touch = e.touches[0];
  if (!touch) return;

  debugSetLastEvent('document:touchmove:fallback', 'touch');
  e.preventDefault();
  updatePreviewAtPoint(touch.clientX, touch.clientY, 'document:touchmove');
}

function onDocumentTouchEndFallback(e: TouchEvent): void {
  if (!active) return;
  if (isOverlayOrWidgetTarget(e.target)) {
    logBugWidgetDebug('document_fallback_touchend_ignored_widget_or_overlay');
    return;
  }
  debugSetLastEvent('document:touchend:fallback', 'touch');
  logBugWidgetDebug('document_fallback_touchend', { touches: e.changedTouches.length });
  const touch = e.changedTouches[0];
  if (!touch) {
    debugSetLastDecision('ignored', 'document-touchend-no-touch');
    return;
  }
  e.preventDefault();
  commitFromTouchInteraction(touch.clientX, touch.clientY, 'document:touchend');
}

function onKeyDown(e: KeyboardEvent): void {
  if (e.key === 'Escape' && active) {
    deactivateSelector('escape');
  }
}

/**
 * Capture-phase handler to block pointer events from reaching the host page.
 * This prevents frameworks like Radix UI from detecting "outside clicks"
 * and closing popups/dialogs when the user clicks to select an element.
 * Safe to use now that the form is in an iframe (won't affect input focus).
 */
function onCapturePointerDown(e: PointerEvent): void {
  if (!active) return;
  if (e.pointerType && e.pointerType !== 'mouse') return;
  if (!isDesktopFinePointerMode()) return;

  debugSetLastEvent('capture:pointerdown', e.pointerType || '');
  if (isWidgetControlTarget(e.target)) {
    logBugWidgetDebug('capture_pointerdown_widget_passthrough');
    return;
  }

  logBugWidgetDebug('desktop_block_pointerdown');
  e.preventDefault();
  e.stopImmediatePropagation();
}

function onCaptureMouseDown(e: MouseEvent): void {
  if (!active) return;
  if (!isDesktopFinePointerMode()) return;

  debugSetLastEvent('capture:mousedown', 'mouse');
  if (isWidgetControlTarget(e.target)) {
    logBugWidgetDebug('capture_mousedown_widget_passthrough');
    return;
  }

  logBugWidgetDebug('desktop_block_mousedown');
  e.preventDefault();
  e.stopImmediatePropagation();
}

function onCaptureClick(e: MouseEvent): void {
  if (!active) return;
  if (!isDesktopFinePointerMode()) return;

  debugSetLastEvent('capture:click', 'mouse');
  if (isWidgetControlTarget(e.target)) {
    logBugWidgetDebug('capture_click_widget_passthrough');
    return;
  }

  logBugWidgetDebug('desktop_block_click');
  e.preventDefault();
  e.stopImmediatePropagation();
  selectFromInteraction(e.clientX, e.clientY, 'capture:click');
}

function createSelectorOverlay(): HTMLDivElement {
  const overlay = document.createElement('div');
  overlay.setAttribute(WIDGET_ATTR, '');
  // Cover the full viewport for element selection.
  // The widget host is now sized to panel area only (380px right) with pointer-events:auto,
  // so it sits above this overlay and receives clicks directly.
  Object.assign(overlay.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    bottom: '0',
    right: '0',
    zIndex: '2147483646',
    pointerEvents: 'auto',
    cursor: 'crosshair',
    background: 'transparent',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  });

  // Direct handlers on overlay are primary path.
  overlay.addEventListener('mousemove', onOverlayMouseMove);
  overlay.addEventListener('mousedown', onOverlayMouseDown);
  overlay.addEventListener('click', onOverlayClick);
  overlay.addEventListener('pointerup', onOverlayPointerUp);
  overlay.addEventListener('touchstart', onOverlayTouchStart, { passive: false });
  overlay.addEventListener('touchmove', onOverlayTouchMove, { passive: false });
  overlay.addEventListener('touchend', onOverlayTouchEnd, { passive: false });

  return overlay;
}

export function activateSelector(): void {
  if (active) return;
  active = true;
  lastSelection = { x: Number.NaN, y: Number.NaN, at: 0 };
  lastPreviewTarget = null;
  debugSetSelectMode(true, 'activate_selector');
  selectorOverlay = createSelectorOverlay();
  document.body.appendChild(selectorOverlay);
  document.addEventListener('keydown', onKeyDown, true);

  // Block pointer/mouse events in capture phase to prevent host frameworks
  // (Radix, etc.) from closing popups when user clicks to select elements.
  // Attach on window so we intercept before document-level outside-click handlers.
  window.addEventListener('pointerdown', onCapturePointerDown, true);
  window.addEventListener('mousedown', onCaptureMouseDown, true);
  window.addEventListener('click', onCaptureClick, true);

  // Fallback listeners for browsers (iOS Safari) where overlay events may be flaky.
  document.addEventListener('click', onDocumentClickFallback, true);
  document.addEventListener('pointerup', onDocumentPointerUpFallback, true);
  document.addEventListener('touchmove', onDocumentTouchMoveFallback, { capture: true, passive: false });
  document.addEventListener('touchend', onDocumentTouchEndFallback, { capture: true, passive: false });

  debugSetSelectedCount(getStore().selectedElements.length);
}

export function deactivateSelector(reason = 'deactivate_selector'): void {
  active = false;
  debugSetSelectMode(false, reason);
  hideHoverHighlight();

  document.removeEventListener('keydown', onKeyDown, true);
  window.removeEventListener('pointerdown', onCapturePointerDown, true);
  window.removeEventListener('mousedown', onCaptureMouseDown, true);
  window.removeEventListener('click', onCaptureClick, true);
  document.removeEventListener('click', onDocumentClickFallback, true);
  document.removeEventListener('pointerup', onDocumentPointerUpFallback, true);
  document.removeEventListener('touchmove', onDocumentTouchMoveFallback, true);
  document.removeEventListener('touchend', onDocumentTouchEndFallback, true);

  if (selectorOverlay) {
    selectorOverlay.remove();
    selectorOverlay = null;
  }

  lastPreviewTarget = null;
}

export function isSelectorActive(): boolean {
  return active;
}

export function setSelectionListener(listener: ((element: SelectedElement) => void) | null): void {
  selectionListener = listener;
}

function captureElementInfo(element: HTMLElement): SelectedElement {
  const rect = element.getBoundingClientRect();
  const computed = window.getComputedStyle(element);

  const styleKeys = ['display', 'visibility', 'opacity', 'z-index', 'overflow', 'color', 'background', 'position'];
  const computedStyles: Record<string, string> = {};
  for (const key of styleKeys) {
    computedStyles[key] = computed.getPropertyValue(key);
  }

  return {
    element,
    selector: generateUniqueSelector(element),
    xpath: generateXPath(element),
    tag: element.tagName.toLowerCase(),
    classes: Array.from(element.classList),
    id: element.id || '',
    text: truncateText(element.innerText || '', 200),
    boundingBox: {
      x: Math.round(rect.x),
      y: Math.round(rect.y),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    },
    computedStyles,
    accessibility: {
      role: element.getAttribute('role'),
      ariaLabel: element.getAttribute('aria-label'),
      tabindex: element.getAttribute('tabindex'),
    },
  };
}
